import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

interface LinksProps {}
const ua = navigator.userAgent.toLowerCase();
const isInApp = /(kakaotalk|fb_iab|instagram|line|naver|daum|whatsapp|twitter)/.test(ua) || /; wv\)/.test(ua); // Android WebView 패턴
//TEST용
const Links: React.FC<LinksProps> = () => {
  const { page } = useParams<{ page: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isAppInstalled, setIsAppInstalled] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const [isTryingApp, setIsTryingApp] = useState<boolean>(false);
  const [isDeepLinkEntry, setIsDeepLinkEntry] = useState<boolean>(false);

  
  // 안드로이드 크롬으로 열기
  
  const openInChrome = async (url: string) => {
    // https://example.com → host/path 분리 보조
    const u = new URL(url);
    
    // intent://{host}{path}{search}{hash}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=...;end
    const intent = `intent://${u.host}${u.pathname}${u.search}${u.hash}` +
      `#Intent;scheme=${u.protocol.replace(':','')};package=com.android.chrome;` +
      // 실패 시 동일 URL을 그냥 엽니다(최소한 페이지는 유지)
      `S.browser_fallback_url=${encodeURIComponent(url)};end`;
    
    // 인앱 대부분은 location.href가 더 안정적
    window.location.href = intent;
  }
  // 페이지와 라우터 매핑 맵 (현재는 모두 /login으로 설정)
  const pageRouteMap: Record<string, string> = {
    home: "/login",
    login: "/login",
    signup: "/login",
    profile: "/login",
    settings: "/login",
    order: "/login",
    payment: "/login",
    mypage: "/login",
    pricing: "/login",
    contact: "/login",
    faq: "/login",
    policy: "/login",
    about: "/login",
    help: "/login",
    support: "/login",
    // 추가 페이지들...
  };

  // 모바일 디바이스 감지
  const detectMobile = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    console.log("Full User-Agent:", userAgent);

    const mobileKeywords = [
      "android",
      "iphone",
      "ipad",
      "ipod",
      "blackberry",
      "windows phone",
    ];
    const isMobile = mobileKeywords.some((keyword) =>
      userAgent.includes(keyword)
    );

    console.log(
      "Detected mobile keywords:",
      mobileKeywords.filter((keyword) => userAgent.includes(keyword))
    );
    console.log("Is mobile:", isMobile);

    return isMobile;
  };

  // window.history 로깅 함수
  const logHistoryState = (action: string) => {
    console.log(`[History Log] ${action}:`, {
      length: window.history.length,
      state: window.history.state,
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      timestamp: new Date().toISOString(),
    });
  };

  // 딥링크 진입 시 히스토리 상태 설정 (웹 브라우저용)
  const setupDeepLinkHistory = () => {
    logHistoryState("딥링크 진입 전");

    // 딥링크 진입 상태로 설정
    setIsDeepLinkEntry(true);

    // 웹 브라우저에서 딥링크 진입 시 히스토리 상태 설정
    // 이전 이력이 없는 경우를 대비해 더미 히스토리 엔트리 추가
    const currentState = window.history.state || {};
    window.history.replaceState(
      { ...currentState, isDeepLinkEntry: true, page, isWebDeepLink: true },
      "",
      window.location.href
    );

    // 딥링크 진입으로 인한 이전 이력이 없는 경우를 대비해 더미 엔트리 추가
    window.history.pushState(
      { isDummyEntry: true, isWebDeepLink: true },
      "",
      window.location.href
    );

    logHistoryState("딥링크 히스토리 설정 후");
  };

  // native backbutton 처리 (웹 브라우저용 최적화)
  const handleNativeBackButton = () => {
    logHistoryState("Native backbutton 처리");

    // 딥링크 진입이었다면 메인 페이지로 이동
    if (isDeepLinkEntry) {
      console.log("[DeepLink] 웹 딥링크 진입 후 뒤로가기 - 메인 페이지로 이동");

      // 웹 브라우저에서 딥링크 진입 시 이전 이력이 없는 경우 메인으로 이동
      const currentState = window.history.state;
      if (currentState && currentState.isWebDeepLink) {
        console.log(
          "[DeepLink] 웹 딥링크 진입으로 인한 이전 이력 없음 - 메인으로 이동"
        );
        navigate("/", { replace: true });
        setIsDeepLinkEntry(false);
        return;
      }

      // 일반적인 딥링크 뒤로가기 처리
      navigate("/", { replace: true });
      setIsDeepLinkEntry(false);
    } else {
      // 일반적인 뒤로가기 처리
      window.history.back();
    }
  };

  // 데스크톱에서 페이지 라우팅 처리
  const handleDesktopRouting = () => {
    setIsRedirecting(true);

    // 딥링크 진입 시 히스토리 설정
    setupDeepLinkHistory();

    if (!page) {
      // page 파라미터가 없으면 기본적으로 /login으로 이동
      setTimeout(() => navigate("/login"), 0);
      return;
    }

    const targetRoute = pageRouteMap[page.toLowerCase()];
    if (targetRoute) {
      // 매핑된 라우트로 이동
      setTimeout(() => navigate(targetRoute), 0);
    } else {
      // 매핑되지 않은 페이지는 기본적으로 /login으로 이동
      console.warn(`Unknown page: ${page}, redirecting to /login`);
      setTimeout(() => navigate("/login"), 0);
    }
  };

  // 앱 설치 여부 확인 및 앱 실행 시도
  const checkAppInstalled = () => {
    const mobile = detectMobile();
    setIsMobile(mobile);

    if (mobile) {
      // 모바일에서 앱 실행 시도
      setIsTryingApp(true);
      attemptAppLaunch();
    } else {
      // 데스크톱에서는 페이지 라우팅 처리
      setIsAppInstalled(false);
      handleDesktopRouting();
    }
  };

  // 앱 실행 시도
  const attemptAppLaunch = () => {
    const appScheme = "drawer://"; // 앱의 커스텀 스킴
    const appLink = `drawer://link/${page}?${searchParams.toString()}`;

    // 앱 실행 시도
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = appScheme;
    document.body.appendChild(iframe);

    // 앱이 설치되어 있으면 실행되고, 없으면 타임아웃 발생
    const timeout = setTimeout(() => {
      // 앱이 설치되지 않았거나 실행되지 않음
      setIsAppInstalled(false);
      setIsTryingApp(false);
      document.body.removeChild(iframe);
      // 앱이 설치되지 않았으면 자동으로 앱 설치 페이지로 이동
      handleAppInstall();
    }, 2000);

    // 앱이 실행되면 페이지가 blur되거나 focus가 변경됨
    const handleBlur = () => {
      clearTimeout(timeout);
      setIsAppInstalled(true);
      setIsTryingApp(false);
      document.body.removeChild(iframe);
      window.removeEventListener("blur", handleBlur);
    };

    window.addEventListener("blur", handleBlur);

    // 추가적인 앱 실행 시도 (직접 링크)
    setTimeout(() => {
      window.location.href = appLink;
    }, 0);
  };

  useEffect(() => {
    checkAppInstalled();

    // popstate 이벤트 리스너 등록 (뒤로가기/앞으로가기 처리)
    const handlePopState = (event: PopStateEvent) => {
      logHistoryState("PopState 이벤트 발생");

      if (event.state && event.state.isDeepLinkEntry) {
        // 딥링크 진입 상태에서 뒤로가기 시 메인 페이지로 이동
        console.log("[DeepLink] PopState - 딥링크 진입 후 뒤로가기");
        navigate("/", { replace: true });
        setIsDeepLinkEntry(false);
      }
    };

    // beforeunload 이벤트 리스너 등록 (페이지 이탈 시 로깅)
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      logHistoryState("BeforeUnload 이벤트 발생");
    };

    // 이벤트 리스너 등록
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [navigate, isDeepLinkEntry]);

  const handleAppLink = () => {
    const appLink = `drawer://link/${page}?${searchParams.toString()}`;

    if (isMobile) {
      // 모바일에서 앱 실행 시도
      attemptAppLaunch();
    } else {
      
      // if(isInApp) {
      //   alert(`인앱 실행`);
      //
      //   return;
      // }
      // 데스크톱에서는 앱 설치 페이지로 이동
      // openInChrome('/app-install')
      window.location.href = "/app-install";
    }
  };

  const handleAppInstall = () => {
    console.log("handleAppInstall called, isMobile:", isMobile);

    if (isMobile) {
      // 모바일에서 앱스토어로 직접 이동
      const userAgent = navigator.userAgent.toLowerCase();
      console.log("Mobile User-Agent:", userAgent);

      if (userAgent.includes("iphone") || userAgent.includes("ipad")) {
        // iOS 앱스토어
        console.log("Redirecting to iOS App Store");
        window.location.href = "https://apps.apple.com/app/drawer"; // 실제 앱스토어 URL로 변경
      } else if (userAgent.includes("android")) {
        // Android 플레이스토어
        console.log("Redirecting to Android Play Store");
        window.location.href =
          "https://play.google.com/store/apps/details?id=com.drawer.app"; // 실제 플레이스토어 URL로 변경
      } else {
        // 기타 모바일 디바이스 - 기본적으로 iOS 앱스토어로 이동
        console.log("Unknown mobile device, redirecting to iOS App Store");
        window.location.href = "https://apps.apple.com/app/drawer";
      }
    } else {
      // 데스크톱에서는 앱 설치 페이지로 이동
      console.log("Desktop device, redirecting to /app-install");
      window.location.href = "/app-install";
    }
  };

  return (
    <></>
  );
};

export default Links;
